import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap } from 'lucide-react';
import { companyInfo, logoAltUrl } from '../data/mock';
import { useSEO } from '../hooks/useSEO';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
      <Zap size={18} className="text-electric-yellow" />
      {title}
    </h2>
    <div className="text-neutral-400 leading-relaxed space-y-3">{children}</div>
  </div>
);

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO(
    'Terms of Service | Nexline Electrical Ltd.',
    "Review the terms and conditions for using the Nexline Electrical Ltd. website and requesting residential or commercial electrical services."
  );

  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Simple top bar */}
      <header className="border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-0 h-24 flex items-center justify-between">
          <Link to="/" data-testid="terms-logo-home-link" className="flex items-center gap-3">
            <img src={logoAltUrl} alt="Nexline Electrical" className="h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="terms-back-home-link"
            className="flex items-center gap-2 text-neutral-400 hover:text-electric-yellow text-sm font-medium transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-16 lg:py-24" data-testid="terms-of-service-page">
        <p className="text-electric-yellow text-sm font-semibold uppercase tracking-wider mb-3">Legal</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Terms of Service</h1>
        <p className="text-neutral-500 mb-14">Last updated: {lastUpdated}</p>

        <Section title="Agreement to Terms">
          <p>
            By accessing or using the {companyInfo.name} website, or by requesting a quote or service from
            us, you agree to be bound by these Terms of Service. If you do not agree with any part of these
            terms, please do not use our website or services.
          </p>
        </Section>

        <Section title="Services">
          <p>
            {companyInfo.name} provides residential and commercial electrical services across Canada,
            including but not limited to panel upgrades, EV charger installation, power distribution, and
            maintenance & repairs. Quotes provided through the website are estimates and subject to change
            following an on-site assessment.
          </p>
        </Section>

        <Section title="Use of Website">
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>You agree to use this website only for lawful purposes.</li>
            <li>You agree not to misuse, copy, or redistribute website content without permission.</li>
            <li>
              Content embedded from third parties (such as Instagram) is subject to that platform's own
              terms.
            </li>
          </ul>
        </Section>

        <Section title="Quotes & Bookings">
          <p>
            Requesting a quote through this website does not guarantee scheduling or availability. All
            projects are subject to a service agreement, applicable permits, and Canadian Electrical Code
            compliance, confirmed directly with our team prior to work commencing.
          </p>
        </Section>

        <Section title="Limitation of Liability">
          <p>
            {companyInfo.name} is not liable for any indirect, incidental, or consequential damages arising
            from the use of this website or reliance on information presented here. All electrical work is
            performed by licensed professionals under separate service agreements and applicable warranties.
          </p>
        </Section>

        <Section title="Intellectual Property">
          <p>
            All logos, images, and content on this website are the property of {companyInfo.name} unless
            otherwise credited, and may not be reproduced without written consent.
          </p>
        </Section>

        <Section title="Changes to These Terms">
          <p>
            We may revise these Terms of Service at any time. Continued use of the website after changes are
            posted constitutes acceptance of the updated terms. The "Last updated" date above reflects the
            most recent revision.
          </p>
        </Section>

        <Section title="Governing Law">
          <p>
            These terms are governed by the laws of Canada and the province in which services are performed,
            without regard to conflict of law principles.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>Questions about these Terms of Service can be directed to:</p>
          <ul className="space-y-1 pl-2">
            <li>
              Email:{' '}
              <a href={`mailto:${companyInfo.email}`} className="text-electric-yellow hover:underline">
                {companyInfo.email}
              </a>
            </li>
            <li>
              Phone:{' '}
              <a href={`tel:${companyInfo.phone}`} className="text-electric-yellow hover:underline">
                {companyInfo.phone}
              </a>
            </li>
            <li>Coverage: {companyInfo.coverage}</li>
          </ul>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/5">
          <Link
            to="/"
            data-testid="terms-back-home-button"
            className="inline-flex items-center gap-2 bg-electric-yellow text-black px-6 py-3 text-base font-medium hover:bg-transparent hover:text-electric-yellow border border-electric-yellow transition-all duration-400"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
